import { readonly, ref } from 'vue';
import Cookies from 'js-cookie';
import WhatsappCallsAPI from 'dashboard/api/channel/whatsapp/whatsappCallsAPI';
import { remuxWebmToOgg } from 'dashboard/components/widgets/WootWriter/utils/webmOpusToOgg';
import { VOICE_CALL_OUTBOUND_INIT_STATUS } from 'dashboard/components-next/message/constants';
import { useCallsStore } from 'dashboard/stores/calls';
import { markCallDismissed } from 'dashboard/helper/voice';

// Module-level state lets the cable handlers and unload listeners reach the
// live PeerConnection without prop-drilling refs through every composable.
let pc = null;
let localStream = null;
let remoteStream = null;
let remoteAudioEl = null;
let mediaRecorder = null;
let recorderChunks = [];
let audioContext = null;
let recordingDestination = null;
let localRecordingSource = null;
let remoteRecordingSource = null;
let activeCallId = null;
// The provider's id of the call this tab placed, which is what a late ringing message names.
let activeCallSid = null;
// voice_call.outbound_connected (the sole source of the outbound SDP answer) is
// broadcast account-wide and can arrive before the /initiate response sets
// activeCallId in this tab. Until we know our own call id we can't tell our
// answer from a concurrent agent's, so the cable handler buffers them here keyed
// by call id (a single slot would let a concurrent agent's event clobber ours)
// and initiateOutboundCall flushes the matching one by id.
const pendingOutboundAnswers = new Map();
// The pickup and the end of a placed call are broadcast too, and on a paired phone they
// can arrive before this tab has the call in its store: the connector answers the dial as
// soon as the phone rings. They are kept here by call id, like the answers above, and
// applied by the calls store when the call is added (takeEarlyOutboundOutcome).
const earlyOutboundOutcomes = new Map();
// Module-scoped so multiple composable callers (header button + contact-panel
// button) share the same lock. A per-instance ref let two parallel callers
// both pass the guard and tear down each other's WebRTC state in cleanup().
// ref() so consumers can reactively gate buttons on it (the composable
// re-exports this as `isInitiating`).
const isInitiatingOutbound = ref(false);
const isInitiatingOutboundReadonly = readonly(isInitiatingOutbound);
// Inbound calls record from the moment the agent clicks accept (their click =
// pickup). Outbound calls must wait — Meta's `connect` webhook (which lands
// during ringing) negotiates remote tracks ~20s before the contact actually
// answers, and we don't want pre-pickup audio in the recording. This flag is
// flipped to true by armOutboundRecorder() when the ACCEPTED status arrives.
let recorderArmed = false;
// This call's recording decision, snapshotted server-side at call creation and
// carried on every payload that can start a call in this tab.
let callRecordingEnabled = true;
let localAudioSender = null;
let intendedMuted = false;
let uplinkMonitoringActive = false;
let uplinkMonitorTimer = null;
let uplinkPollInFlight = false;
let uplinkMonitorGeneration = 0;
let uplinkRecoveryAttempts = 0;
let uplinkRecoveryPromise = null;
let uplinkFailureInProgress = false;
let uplinkStalledSince = null;
let uplinkPreviousStats = null;
let uplinkStats = {
  bytesSent: 0,
  packetsSent: 0,
  bytesAdvancing: false,
  packetsAdvancing: false,
  progress: 'unavailable',
};
let uplinkIssueSequence = 0;

export const WHATSAPP_CALL_UPLINK_ERROR = Object.freeze({
  PERMISSION_DENIED: 'permission_denied',
  NO_INPUT: 'no_input',
  TRACK_ENDED: 'track_ended',
  STALLED: 'stalled',
});

const uplinkIssue = ref(null);
export const whatsappCallUplinkIssue = readonly(uplinkIssue);

export const UPLINK_STATS_INTERVAL_MS = 2000;
export const UPLINK_STALL_LIMIT_MS = 8000;
export const UPLINK_MAX_RECOVERY_ATTEMPTS = 1;

export const getWhatsappCallErrorMessage = (errorOrIssue, translate) => {
  switch (errorOrIssue?.code) {
    case WHATSAPP_CALL_UPLINK_ERROR.PERMISSION_DENIED:
      return translate(
        'CONVERSATION.HEADER.WHATSAPP_CALL_MIC_PERMISSION_DENIED'
      );
    case WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT:
      return translate('CONVERSATION.HEADER.WHATSAPP_CALL_NO_MICROPHONE');
    case WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED:
      return translate('CONVERSATION.HEADER.WHATSAPP_CALL_MICROPHONE_ENDED');
    case WHATSAPP_CALL_UPLINK_ERROR.STALLED:
      return translate('CONVERSATION.HEADER.WHATSAPP_CALL_UPLINK_STALLED');
    default:
      return null;
  }
};

const ensureRemoteAudioElement = () => {
  if (remoteAudioEl) return remoteAudioEl;
  remoteAudioEl = document.createElement('audio');
  remoteAudioEl.id = 'whatsapp-call-remote-audio';
  remoteAudioEl.autoplay = true;
  remoteAudioEl.playsInline = true;
  remoteAudioEl.style.display = 'none';
  document.body.appendChild(remoteAudioEl);
  return remoteAudioEl;
};

const playRemoteStream = stream => {
  const el = ensureRemoteAudioElement();
  el.srcObject = stream;
  el.play().catch(err => {
    // eslint-disable-next-line no-console
    console.warn('[WhatsApp Call] remote audio play() failed:', err);
  });
};

// 1s timeslice keeps a recent recording chunk in memory so a remote hangup
// that races cleanup still has data to upload.
const RECORDING_TIMESLICE_MS = 1000;
const ICE_GATHER_TIMEOUT_MS = 10000;
// The OGG remux is a whole-file in-memory pass on the main thread (~4x the
// blob size transient); past this cap (~3 h at the 48 kbps recording bitrate)
// skip it and upload the raw WebM instead.
const MAX_REMUX_BYTES = 64 * 1024 * 1024;

const RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
];

// Outbound calls have no backend-supplied ice_servers (the call doesn't exist
// at offer time). Without STUN the browser only sends host candidates and
// browser→Meta media silently drops through any non-trivial NAT.
const DEFAULT_OUTBOUND_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

const waitForIceGatheringComplete = peer =>
  new Promise(resolve => {
    if (peer.iceGatheringState === 'complete') {
      resolve();
      return;
    }
    const timer = setTimeout(resolve, ICE_GATHER_TIMEOUT_MS);
    peer.addEventListener('icegatheringstatechange', () => {
      if (peer.iceGatheringState === 'complete') {
        clearTimeout(timer);
        resolve();
      }
    });
  });

const createMicrophoneError = code => {
  const error = new Error('WhatsApp call microphone is unavailable');
  error.name = 'WhatsappCallMicrophoneError';
  error.code = code;
  return error;
};

const microphoneErrorFrom = error => {
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') {
    return createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.PERMISSION_DENIED);
  }
  return createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
};

const stopStream = stream => {
  stream?.getTracks().forEach(track => track.stop());
};

const getLocalAudioTrack = () => localStream?.getAudioTracks()[0] || null;

const isUsableAudioTrack = track =>
  track?.kind === 'audio' && track.readyState === 'live';

const isPeerConnected = peer =>
  peer?.connectionState === 'connected' ||
  peer?.iceConnectionState === 'connected' ||
  peer?.iceConnectionState === 'completed';

const resetUplinkStats = () => {
  uplinkStalledSince = null;
  uplinkPreviousStats = null;
  uplinkStats = {
    bytesSent: 0,
    packetsSent: 0,
    bytesAdvancing: false,
    packetsAdvancing: false,
    progress: 'unavailable',
  };
};

const stopUplinkMonitor = () => {
  uplinkMonitorGeneration += 1;
  if (uplinkMonitorTimer) clearInterval(uplinkMonitorTimer);
  uplinkMonitorTimer = null;
  uplinkPollInFlight = false;
  resetUplinkStats();
};

const publishUplinkIssue = code => {
  uplinkIssueSequence += 1;
  uplinkIssue.value = { id: uplinkIssueSequence, code };
};

const audioSenderStats = report => {
  let bytesSent = 0;
  let packetsSent = 0;
  let found = false;
  report?.forEach(stat => {
    if (
      stat.type !== 'outbound-rtp' ||
      (stat.kind && stat.kind !== 'audio') ||
      (stat.mediaType && stat.mediaType !== 'audio')
    ) {
      return;
    }
    found = true;
    bytesSent += Number(stat.bytesSent) || 0;
    packetsSent += Number(stat.packetsSent) || 0;
  });
  return found ? { bytesSent, packetsSent } : null;
};

const connectLocalRecorderSource = () => {
  if (!audioContext || !recordingDestination || !localStream) return;
  localRecordingSource?.disconnect?.();
  localRecordingSource = audioContext.createMediaStreamSource(localStream);
  localRecordingSource.connect(recordingDestination);
};

const setupRecorder = () => {
  if (!callRecordingEnabled) return;
  if (!localStream || !remoteStream || mediaRecorder) return;
  // createMediaStreamSource on a stream with no audio tracks wires up to
  // nothing — the recorded mix would be silence. Wait until ontrack fires.
  if (remoteStream.getAudioTracks().length === 0) return;

  audioContext = new AudioContext({ sampleRate: 48000 });
  // AudioContext starts suspended under most autoplay policies; without
  // resume() the destination stream produces silence.
  audioContext.resume().catch(() => {});

  recordingDestination = audioContext.createMediaStreamDestination();
  connectLocalRecorderSource();
  remoteRecordingSource = audioContext.createMediaStreamSource(remoteStream);
  remoteRecordingSource.connect(recordingDestination);

  const mimeType = RECORDER_MIME_CANDIDATES.find(t =>
    MediaRecorder.isTypeSupported(t)
  );
  if (!mimeType) return;

  recorderChunks = [];
  // 48 kbps Opus is transparent for speech vs Chrome's ~128 kbps default.
  mediaRecorder = new MediaRecorder(recordingDestination.stream, {
    mimeType,
    audioBitsPerSecond: 48000,
  });
  mediaRecorder.ondataavailable = event => {
    if (event.data && event.data.size > 0) recorderChunks.push(event.data);
  };
  mediaRecorder.start(RECORDING_TIMESLICE_MS);
};

const cleanup = () => {
  uplinkMonitoringActive = false;
  stopUplinkMonitor();
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    try {
      mediaRecorder.stop();
    } catch (_) {
      /* noop */
    }
  }
  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close().catch(() => {});
  }
  stopStream(localStream);
  stopStream(remoteStream);
  if (pc) pc.close();
  if (remoteAudioEl) remoteAudioEl.srcObject = null;

  pc = null;
  // Outcomes are kept through a teardown: an end that tore the session down while the
  // dial was still being answered is what keeps the call it placed from being added
  // back as ringing. A new dial starts them over.
  localStream = null;
  remoteStream = null;
  mediaRecorder = null;
  callRecordingEnabled = true;
  recorderChunks = [];
  audioContext = null;
  recordingDestination = null;
  localRecordingSource = null;
  remoteRecordingSource = null;
  activeCallId = null;
  activeCallSid = null;
  localAudioSender = null;
  intendedMuted = false;
  uplinkRecoveryAttempts = 0;
  uplinkRecoveryPromise = null;
  uplinkFailureInProgress = false;
  pendingOutboundAnswers.clear();
  recorderArmed = false;
  // eslint-disable-next-line no-use-before-define
  stopRingingTimer();
};

const buildPeerConnection = iceServers => {
  const config = iceServers && iceServers.length ? { iceServers } : {};
  pc = new RTCPeerConnection(config);
  remoteStream = new MediaStream();
  pc.ontrack = event => {
    // Reuse the same MediaStream object — the recorder's audioContext source
    // taps it once, so reassigning would orphan the recorder.
    const tracks =
      event.streams && event.streams[0]
        ? event.streams[0].getTracks()
        : [event.track];
    tracks.forEach(track => {
      if (!remoteStream.getTracks().includes(track))
        remoteStream.addTrack(track);
    });
    playRemoteStream(remoteStream);
    // Only arm the recorder when the call is actually accepted. For outbound
    // this is the ACCEPTED status webhook; for inbound this is the agent's
    // own click (acceptIncomingCall flips recorderArmed before returning).
    if (recorderArmed) setupRecorder();
  };
  // A transport that failed carries nothing more, and the provider may never say the call
  // ended: a paired phone's connector hangs up a call it can no longer carry without
  // always reaching the client.
  const own = pc;
  own.onconnectionstatechange = () => {
    if (own !== pc) return;
    if (own.connectionState === 'failed' && activeCallId) {
      // eslint-disable-next-line no-use-before-define
      letGoOfCall(activeCallId, activeCallSid);
      return;
    }
    // eslint-disable-next-line no-use-before-define
    syncUplinkMonitorWithCallState();
  };
  own.oniceconnectionstatechange = () => {
    if (own !== pc) return;
    // eslint-disable-next-line no-use-before-define
    syncUplinkMonitorWithCallState();
  };
  return pc;
};

// A call this tab can no longer expect to hear the end of. The end is asked for, as an
// agent hanging up would, and this tab lets go of the call itself without waiting on that
// request: the end broadcast is not replayed to a tab whose cable was down, a failed
// request sends none, and one that hangs would hold the microphone for as long as it
// does. The recording is uploaded, the session released and the card removed, once,
// however many of these and the broadcast arrive.
const letGoOfCall = (callId, placedSid) => {
  // Kept before the teardown, as an end broadcast keeps it: a dial still applying its
  // answer returns this call afterwards, and must not have it added back as ringing.
  // eslint-disable-next-line no-use-before-define
  noteEarlyOutboundOutcome(callId, 'ended');
  WhatsappCallsAPI.terminate(callId).catch(() => {});
  // eslint-disable-next-line no-use-before-define
  return handleWhatsappRemoteEnd(callId)
    .catch(() => {})
    .then(() => {
      const store = useCallsStore();
      const card = store.calls.find(c => c.callId === callId);
      // Dismissed as an end broadcast would, so a ringing message still queued for the
      // call does not add it back.
      markCallDismissed(card?.callSid || placedSid);
      if (card) store.removeCall(card.callSid);
    });
};

const attachLocalTrackEndedHandler = track => {
  track.onended = () => {
    if (track !== getLocalAudioTrack() || !uplinkMonitoringActive) return;
    const peer = pc;
    // eslint-disable-next-line no-use-before-define
    recoverLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED).catch(
      error => {
        // eslint-disable-next-line no-use-before-define
        if (peer === pc) failActiveCallForUplink(error.code);
      }
    );
  };
};

const acquireLocalAudioStream = async () => {
  let stream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (error) {
    throw microphoneErrorFrom(error);
  }

  const audioTracks = stream.getAudioTracks();
  if (audioTracks.length !== 1) {
    stopStream(stream);
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
  }

  const track = audioTracks[0];
  if (!isUsableAudioTrack(track)) {
    stopStream(stream);
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
  }

  track.enabled = !intendedMuted;
  if (!intendedMuted && !track.enabled) {
    stopStream(stream);
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
  }
  return { stream, track };
};

const setInitialLocalAudioStream = async () => {
  const acquired = await acquireLocalAudioStream();
  localStream = acquired.stream;
  attachLocalTrackEndedHandler(acquired.track);
  return acquired.track;
};

const validateLocalAudioTrack = () => {
  const audioTracks = localStream?.getAudioTracks() || [];
  if (audioTracks.length !== 1) {
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
  }
  const track = audioTracks[0];
  if (!isUsableAudioTrack(track)) {
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
  }
  track.enabled = !intendedMuted;
  if (!intendedMuted && !track.enabled) {
    throw createMicrophoneError(WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
  }
  return track;
};

const replaceLocalAudioTrack = async failureCode => {
  const sender = localAudioSender;
  if (!sender?.replaceTrack) {
    throw createMicrophoneError(failureCode);
  }

  const previousStream = localStream;
  const previousTrack = getLocalAudioTrack();
  const acquired = await acquireLocalAudioStream();
  if (sender !== localAudioSender || previousStream !== localStream || !pc) {
    stopStream(acquired.stream);
    throw createMicrophoneError(failureCode);
  }
  try {
    await sender.replaceTrack(acquired.track);
  } catch (_) {
    stopStream(acquired.stream);
    throw createMicrophoneError(failureCode);
  }
  if (sender !== localAudioSender || previousStream !== localStream || !pc) {
    stopStream(acquired.stream);
    throw createMicrophoneError(failureCode);
  }

  if (previousStream?.removeTrack && previousStream?.addTrack) {
    previousStream.removeTrack(previousTrack);
    previousStream.addTrack(acquired.track);
    acquired.stream.removeTrack?.(acquired.track);
    stopStream(acquired.stream);
    localStream = previousStream;
  } else {
    localStream = acquired.stream;
  }
  attachLocalTrackEndedHandler(acquired.track);
  connectLocalRecorderSource();
  previousTrack?.stop();
  resetUplinkStats();
  return acquired.track;
};

const recoverLocalAudioTrack = failureCode => {
  if (uplinkRecoveryPromise) return uplinkRecoveryPromise;
  if (uplinkRecoveryAttempts >= UPLINK_MAX_RECOVERY_ATTEMPTS) {
    return Promise.reject(createMicrophoneError(failureCode));
  }
  uplinkRecoveryAttempts += 1;
  const recovery = replaceLocalAudioTrack(failureCode);
  uplinkRecoveryPromise = recovery;
  return recovery.finally(() => {
    if (uplinkRecoveryPromise === recovery) uplinkRecoveryPromise = null;
  });
};

const ensureLocalAudioTrack = async failureCode => {
  try {
    return validateLocalAudioTrack();
  } catch (error) {
    return recoverLocalAudioTrack(error.code || failureCode);
  }
};

const failActiveCallForUplink = code => {
  if (uplinkFailureInProgress || !pc) return;
  uplinkFailureInProgress = true;
  uplinkMonitoringActive = false;
  stopUplinkMonitor();
  publishUplinkIssue(code || WHATSAPP_CALL_UPLINK_ERROR.NO_INPUT);
  if (activeCallId) {
    letGoOfCall(activeCallId, activeCallSid);
  } else {
    cleanup();
  }
};

const pollUplinkStats = async () => {
  if (uplinkPollInFlight) return;
  if (!uplinkMonitoringActive || !isPeerConnected(pc) || intendedMuted) {
    resetUplinkStats();
    return;
  }

  const track = getLocalAudioTrack();
  if (!isUsableAudioTrack(track) || !track.enabled) {
    recoverLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED).catch(
      error => failActiveCallForUplink(error.code)
    );
    return;
  }
  const sender = localAudioSender;
  if (!sender?.getStats) return;

  const generation = uplinkMonitorGeneration;
  uplinkPollInFlight = true;
  try {
    const sample = audioSenderStats(await sender.getStats());
    if (generation !== uplinkMonitorGeneration || sender !== localAudioSender) {
      return;
    }
    if (!sample) {
      uplinkStats = { ...uplinkStats, progress: 'unavailable' };
      return;
    }

    const previous = uplinkPreviousStats;
    const countersReset =
      previous &&
      (sample.bytesSent < previous.bytesSent ||
        sample.packetsSent < previous.packetsSent);
    const bytesAdvancing = !!previous && sample.bytesSent > previous.bytesSent;
    const packetsAdvancing =
      !!previous && sample.packetsSent > previous.packetsSent;
    const advancing = bytesAdvancing && packetsAdvancing;
    let progress = 'checking';
    if (previous && !countersReset)
      progress = advancing ? 'advancing' : 'stalled';
    uplinkStats = {
      ...sample,
      bytesAdvancing,
      packetsAdvancing,
      progress,
    };
    uplinkPreviousStats = sample;

    if (!previous || countersReset || advancing) {
      uplinkStalledSince = advancing ? null : Date.now();
      return;
    }
    if (uplinkStalledSince == null) uplinkStalledSince = Date.now();
    if (Date.now() - uplinkStalledSince < UPLINK_STALL_LIMIT_MS) return;

    try {
      await recoverLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.STALLED);
      if (
        generation === uplinkMonitorGeneration &&
        sender === localAudioSender
      ) {
        resetUplinkStats();
      }
    } catch (error) {
      if (
        generation === uplinkMonitorGeneration &&
        sender === localAudioSender
      ) {
        failActiveCallForUplink(error.code);
      }
    }
  } catch (_) {
    if (generation === uplinkMonitorGeneration) {
      uplinkStats = { ...uplinkStats, progress: 'unavailable' };
    }
  } finally {
    if (generation === uplinkMonitorGeneration) uplinkPollInFlight = false;
  }
};

const syncUplinkMonitorWithCallState = () => {
  if (!uplinkMonitoringActive || !isPeerConnected(pc) || !localAudioSender) {
    stopUplinkMonitor();
    return;
  }
  if (uplinkMonitorTimer) return;
  resetUplinkStats();
  pollUplinkStats();
  uplinkMonitorTimer = setInterval(pollUplinkStats, UPLINK_STATS_INTERVAL_MS);
};

// WhatsApp stops ringing an unanswered call well within this. A placed call still
// ringing past it lost its end on the way: a paired phone's connector that lost its
// session ends its calls without always reaching the client, and before the callee
// answers there is no transport yet whose failure would say so.
export const OUTBOUND_RINGING_LIMIT_MS = 90 * 1000;
let ringingTimer = null;
export const CALL_STATUS_LOOKUP_LIMIT_MS = 10 * 1000;

const stopRingingTimer = () => {
  if (ringingTimer) clearTimeout(ringingTimer);
  ringingTimer = null;
};

const startRingingTimer = (callId, placedSid) => {
  stopRingingTimer();
  ringingTimer = setTimeout(async () => {
    ringingTimer = null;
    if (activeCallId !== callId) return;
    // The pickup broadcast can be what was lost, on a cable that dropped after the answer
    // got through: the call is asked about before it is taken for unanswered.
    // Bounded: a request that hangs would leave the call it is about holding the microphone.
    let status = null;
    try {
      ({ status } = await Promise.race([
        WhatsappCallsAPI.show(callId),
        new Promise((_, reject) => {
          setTimeout(reject, CALL_STATUS_LOOKUP_LIMIT_MS);
        }),
      ]));
    } catch (_) {
      /* unreachable as well, or too slow to say: nothing says the call is up */
    }
    // A pickup that arrived, answer applied, while the call was being asked about.
    if (activeCallId !== callId || (recorderArmed && pc?.remoteDescription))
      return;
    // Up on WhatsApp is only a call here once its answer was applied: without one there is
    // no media, and no transport whose failure would ever report the call lost.
    if (status === 'in-progress' && pc?.remoteDescription) {
      const store = useCallsStore();
      const card = store.calls.find(c => c.callId === callId);
      if (card) store.setCallActive(card.callSid);
      // eslint-disable-next-line no-use-before-define
      armOutboundRecorder();
      return;
    }
    letGoOfCall(callId, placedSid);
  }, OUTBOUND_RINGING_LIMIT_MS);
};

const uploadRecording = async (callId, chunks) => {
  let blob = new Blob(chunks, { type: chunks[0].type });
  const isWebm = blob.type.startsWith('audio/webm');
  let filename = isWebm ? 'call-recording.webm' : 'call-recording.ogg';
  // Remux to OGG so the file carries a real duration (MediaRecorder never
  // backfills the WebM duration header, which breaks mobile players).
  if (isWebm && blob.size <= MAX_REMUX_BYTES) {
    try {
      blob = await remuxWebmToOgg(blob);
      filename = 'call-recording.ogg';
    } catch (_) {
      /* noop — fall back to uploading the raw WebM */
    }
  }
  // Best-effort — the controller's idempotency guard handles a retry.
  try {
    await WhatsappCallsAPI.uploadRecording(callId, blob, filename);
  } catch (_) {
    /* noop */
  }
};

// The recorder is stopped and what it recorded is taken here; the upload goes on by itself.
// Waiting on it would keep the microphone, the session and the card for as long as the
// upload takes, and an upload has no ceiling.
const stopRecorderAndUpload = async callId => {
  if (mediaRecorder && mediaRecorder.state !== 'inactive') {
    await new Promise(resolve => {
      mediaRecorder.addEventListener('stop', resolve, { once: true });
      try {
        mediaRecorder.stop();
      } catch (_) {
        resolve();
      }
    });
  }
  if (!recorderChunks.length || !callId) return;

  uploadRecording(callId, recorderChunks.slice());
};

// devise-token-auth requires access-token / client / uid headers on every
// request — navigator.sendBeacon can't set custom headers, so we rehydrate
// the auth payload from the cw_d_session_info cookie that the dashboard sets
// at login. Used by the page-close terminate path below.
const getDeviseAuthHeaders = () => {
  try {
    const raw = Cookies.get('cw_d_session_info');
    if (!raw) return null;
    const session = JSON.parse(raw);
    return {
      'access-token': session['access-token'] || '',
      client: session.client || '',
      uid: session.uid || '',
      expiry: session.expiry || '',
      'token-type': session['token-type'] || 'Bearer',
    };
  } catch (_) {
    return null;
  }
};

const beaconTerminate = callId => {
  if (!callId) return;
  const accountId = window.location.pathname.split('/')[3];
  if (!accountId) return;
  const headers = getDeviseAuthHeaders();
  if (!headers) return;
  const url = `/api/v1/accounts/${accountId}/whatsapp_calls/${callId}/terminate`;
  // fetch+keepalive (instead of navigator.sendBeacon) so we can attach auth
  // headers — without them devise-token-auth 401s and the call stays open on
  // Meta until its carrier-side timeout (~60s).
  try {
    fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: '{}',
    }).catch(() => {});
  } catch (_) {
    /* noop */
  }
};

export const hasActiveWhatsappCall = () => !!(activeCallId || pc);

export const isLocalWhatsappCall = callId =>
  !!callId && activeCallId != null && callId === activeCallId;

export function useWhatsappCallSession() {
  const prepareInboundAnswer = async (sdpOffer, iceServers) => {
    cleanup();
    uplinkIssue.value = null;
    const localTrack = await setInitialLocalAudioStream();
    buildPeerConnection(iceServers);
    localAudioSender = pc.addTrack(localTrack, localStream);
    await pc.setRemoteDescription({ type: 'offer', sdp: sdpOffer });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  const prepareOutboundOffer = async () => {
    cleanup();
    uplinkIssue.value = null;
    const localTrack = await setInitialLocalAudioStream();
    buildPeerConnection(DEFAULT_OUTBOUND_ICE_SERVERS);
    localAudioSender = pc.addTrack(localTrack, localStream);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    await waitForIceGatheringComplete(pc);
    return pc.localDescription.sdp;
  };

  const acceptIncomingCall = async ({
    callId,
    sdpOffer,
    iceServers,
    recordingEnabled,
  }) => {
    // The store may not have sdpOffer yet (the cable broadcast can race the
    // click). Fall back to GET /whatsapp_calls/:id which exposes it.
    let offer = sdpOffer;
    let ice = iceServers;
    let recording = recordingEnabled;
    if (!offer && callId) {
      try {
        const fresh = await WhatsappCallsAPI.show(callId);
        offer = fresh?.sdp_offer || fresh?.sdpOffer;
        ice = ice || fresh?.ice_servers || fresh?.iceServers;
        recording = recording ?? fresh?.recording_enabled;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(
          '[WhatsApp Call] failed to fetch call data for accept:',
          e
        );
      }
    }
    if (!offer) {
      throw new Error('Missing sdp_offer for accept — call may have ended.');
    }

    // Release the mic + peer connection if anything between here and the accept
    // round-trip fails — otherwise a rejected accept leaves the mic live and
    // activeCallId set. Mirrors rejectIncomingCall's self-cleanup; rethrow so
    // the caller can surface the failure and skip marking the call active.
    try {
      const sdpAnswer = await prepareInboundAnswer(offer, ice);
      activeCallId = callId;
      // Inbound: agent's click is the pickup. Arm the recorder before the API
      // round-trip so when ontrack fires (triggered by setRemoteDescription
      // back in prepareInboundAnswer) the recorder is already authorized.
      callRecordingEnabled = recording !== false;
      recorderArmed = true;
      setupRecorder();
      await ensureLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
      await WhatsappCallsAPI.accept(callId, sdpAnswer);
      await ensureLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
      uplinkMonitoringActive = true;
      syncUplinkMonitorWithCallState();
    } catch (e) {
      if (activeCallId === callId) {
        WhatsappCallsAPI.terminate(callId).catch(() => {});
      }
      cleanup();
      throw e;
    }
  };

  const rejectIncomingCall = async callId => {
    try {
      await WhatsappCallsAPI.reject(callId);
    } finally {
      cleanup();
    }
  };

  // target: { conversationId } or { contactId, inboxId }
  const initiateOutboundCall = async target => {
    // Module-scoped lock + active-session guard so a second click — from the
    // same composable instance OR a different one (header vs contact panel)
    // OR while a call is already live — can't tear down the in-flight setup
    // via prepareOutboundOffer's cleanup().
    if (isInitiatingOutbound.value)
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    if (hasActiveWhatsappCall())
      return { status: VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED };
    isInitiatingOutbound.value = true;
    earlyOutboundOutcomes.clear();
    try {
      const sdpOffer = await prepareOutboundOffer();
      await ensureLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
      const response = await WhatsappCallsAPI.initiate(target, sdpOffer);
      if (response?.id) {
        if (!pc || earlyOutboundOutcomes.get(response.id) === 'ended') {
          return response;
        }
        activeCallId = response.id;
        activeCallSid = response.call_id || null;
        // Timed until a pickup with its answer applied hands the call to the transport; a
        // pickup that raced the dial is settled once the buffered answer below is applied.
        startRingingTimer(activeCallId, activeCallSid);
        callRecordingEnabled = response.recording_enabled !== false;
        // A connect webhook that raced ahead of this response was buffered;
        // apply our own by id now that we know it, then drop every buffered
        // answer (concurrent agents' calls aren't ours to apply).
        const buffered = pendingOutboundAnswers.get(activeCallId);
        pendingOutboundAnswers.clear();
        if (buffered) {
          await pc.setRemoteDescription({
            type: 'answer',
            sdp: buffered,
          });
        }
        if (!pc || earlyOutboundOutcomes.get(response.id) === 'ended') {
          return response;
        }
        await ensureLocalAudioTrack(WHATSAPP_CALL_UPLINK_ERROR.TRACK_ENDED);
        if (recorderArmed && pc?.remoteDescription) stopRingingTimer();
        syncUplinkMonitorWithCallState();
        return response;
      }
      // No call id back: this is the permission-request branch. The mic +
      // PeerConnection allocated by prepareOutboundOffer aren't useful until
      // the contact opts in and the agent retries — release them.
      cleanup();
      return response;
    } catch (e) {
      const callId = activeCallId;
      cleanup();
      if (callId) WhatsappCallsAPI.terminate(callId).catch(() => {});
      // BE returns 422 when the contact hasn't opted in (permission template
      // sent or already pending). Surface it to the caller as a normal
      // response shape so it can render the banner instead of an error toast.
      const data = e?.response?.data;
      if (
        data?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_REQUESTED ||
        data?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
      ) {
        return { status: data.status, conversation_id: data.conversation_id };
      }
      throw e;
    } finally {
      isInitiatingOutbound.value = false;
    }
  };

  // callIdOverride is the call.id from the dashboard's calls store. Module
  // `activeCallId` may be null after a prior accept attempt's cleanup() — but
  // the call still exists on Meta and must still be terminated. Falling back
  // to the override means hangup is robust to a wiped local session.
  const endActiveCall = async (callIdOverride = null) => {
    const callId = activeCallId || callIdOverride;
    if (!callId) {
      cleanup();
      return;
    }
    try {
      // Terminate on Meta first so the contact is disconnected immediately. The
      // recording upload below can be slow on long calls or poor networks, and
      // the peer connection / mic must not stay live for the contact during it.
      await WhatsappCallsAPI.terminate(callId).catch(() => {});
      await stopRecorderAndUpload(callId);
    } finally {
      cleanup();
    }
  };

  return {
    isInitiating: isInitiatingOutboundReadonly,
    prepareInboundAnswer,
    prepareOutboundOffer,
    acceptIncomingCall,
    rejectIncomingCall,
    initiateOutboundCall,
    endActiveCall,
  };
}

// Cable handlers fire outside any composable instance, so the shared session
// surface is exposed as module-level functions for them.

export const applyOutboundAnswer = async (callId, sdpAnswer) => {
  // No in-flight peer connection in this tab → not our call.
  if (!pc) return;
  // /initiate hasn't returned our call id yet (the connect webhook raced
  // ahead). Buffer the answer; initiateOutboundCall flushes it by id once it
  // knows which call is ours, so a concurrent agent's answer is never applied.
  if (activeCallId == null) {
    pendingOutboundAnswers.set(callId, sdpAnswer);
    return;
  }
  // activeCallId known → only apply the answer for this tab's call.
  if (callId !== activeCallId) return;
  await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
  // A pickup that came before its answer left the watchdog running for this.
  if (recorderArmed) stopRingingTimer();
  syncUplinkMonitorWithCallState();
};

// Called by the cable handler when Meta delivers status=ACCEPTED for the
// outbound call (real pickup). Flips the recorder gate and starts the
// MediaRecorder. Idempotent — safe if ontrack hasn't fired yet (setupRecorder
// bails until the remote stream has audio tracks; ontrack will retry).
export const armOutboundRecorder = () => {
  // Picked up, and with the answer applied the transport is negotiating: from here a lost
  // call is its to report. Without the answer it never negotiates and never fails, so the
  // watchdog stays until the answer is applied.
  if (pc?.remoteDescription) stopRingingTimer();
  recorderArmed = true;
  uplinkMonitoringActive = true;
  setupRecorder();
  syncUplinkMonitorWithCallState();
};

export const cleanupWhatsappSession = () => cleanup();

// outcome: 'accepted' or 'ended'. Kept only while this tab is placing a call whose id it
// does not know yet, or knows and has not added yet; anything else is not this tab's.
export const noteEarlyOutboundOutcome = (callId, outcome) => {
  if (!pc || (activeCallId != null && activeCallId !== callId)) return;
  if (earlyOutboundOutcomes.get(callId) === 'ended') return;
  earlyOutboundOutcomes.set(callId, outcome);
};

// An end alone, for a call this tab no longer owns: a session torn down by the end of
// the call it was placing. Any other outcome stays where it is.
export const takeEarlyOutboundEnd = callId => {
  if (earlyOutboundOutcomes.get(callId) !== 'ended') return undefined;
  earlyOutboundOutcomes.delete(callId);
  return 'ended';
};

export const takeEarlyOutboundOutcome = callId => {
  const outcome = earlyOutboundOutcomes.get(callId);
  earlyOutboundOutcomes.delete(callId);
  return outcome;
};

// One end of a call can be asked for twice: by its broadcast, and by the store adding a
// call whose end it kept. The second waits for the first instead of tearing down the
// recorder whose upload the first is still finishing.
let ending = null;

export const handleWhatsappRemoteEnd = callId => {
  // Snapshot before cleanup nulls activeCallId.
  const id = callId || activeCallId;
  if (!id) {
    cleanup();
    return Promise.resolve();
  }
  if (ending?.id === id) return ending.promise;
  const promise = (async () => {
    try {
      await stopRecorderAndUpload(id);
    } finally {
      cleanup();
      ending = null;
    }
  })();
  ending = { id, promise };
  return promise;
};

export const setWhatsappCallMuted = muted => {
  intendedMuted = !!muted;
  const track = getLocalAudioTrack();
  if (isUsableAudioTrack(track)) track.enabled = !intendedMuted;
  resetUplinkStats();
  syncUplinkMonitorWithCallState();
  return isUsableAudioTrack(track) ? !track.enabled : intendedMuted;
};

export const getWhatsappCallUplinkDiagnostics = () => {
  const track = getLocalAudioTrack();
  return {
    sessionActive: !!pc,
    monitoringActive: uplinkMonitoringActive,
    peerConnectionState: pc?.connectionState || 'none',
    iceConnectionState: pc?.iceConnectionState || 'none',
    senderPresent: !!localAudioSender,
    trackPresent: !!track,
    trackReadyState: track?.readyState || 'none',
    trackLive: isUsableAudioTrack(track),
    trackEnabled: !!track?.enabled,
    trackMuted: !!track?.muted,
    intentionallyMuted: intendedMuted,
    bytesSent: uplinkStats.bytesSent,
    packetsSent: uplinkStats.packetsSent,
    bytesAdvancing: uplinkStats.bytesAdvancing,
    packetsAdvancing: uplinkStats.packetsAdvancing,
    progress: uplinkStats.progress,
    stalledForMs:
      uplinkStalledSince == null ? 0 : Date.now() - uplinkStalledSince,
    recoveryAttempts: uplinkRecoveryAttempts,
    issue: uplinkIssue.value?.code || 'none',
  };
};

export const sendWhatsappTerminateBeacon = () => {
  // Always fire when there's a live callId. The beacon endpoint is idempotent,
  // so racing it with an in-flight Axios terminate (which unload may abort) is
  // fine — Meta gets exactly one terminate either way, and we avoid leaving
  // the call ringing on Meta until its carrier-side timeout.
  if (!activeCallId) return;
  beaconTerminate(activeCallId);
};

if (typeof window !== 'undefined') {
  window.getWhatsappCallUplinkDiagnostics = getWhatsappCallUplinkDiagnostics;
}
